import DOMPurify from "dompurify";

// Default configuration for most content
const defaultConfig = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "strong",
    "em",
    "u",
    "b",
    "i",
    "span",
    "div",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
    "a",
    "img",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    "blockquote",
    "pre",
    "code",
  ],
  ALLOWED_ATTR: [
    "href",
    "src",
    "alt",
    "title",
    "class",
    "id",
    "target",
    "rel",
    "style",
    "width",
    "height",
  ],
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
};

// Strict configuration for user-generated content
const strictConfig = {
  ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "b", "i", "span"],
  ALLOWED_ATTR: ["class"],
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
};

// Rich content configuration for contracts and CMS content
const richConfig = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "strong",
    "em",
    "u",
    "b",
    "i",
    "span",
    "div",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
    "a",
    "img",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    "blockquote",
    "pre",
    "code",
    "font",
    "center",
    "sub",
    "sup",
  ],
  ALLOWED_ATTR: [
    "href",
    "src",
    "alt",
    "title",
    "class",
    "id",
    "target",
    "rel",
    "style",
    "width",
    "height",
    "color",
    "size",
    "face",
  ],
  ALLOW_DATA_ATTR: false,
  ALLOW_UNKNOWN_PROTOCOLS: false,
};

/**
 * Sanitize HTML content with specified configuration
 * @param {string} html - The HTML content to sanitize
 * @param {string} configType - Configuration type: 'default', 'strict', or 'rich'
 * @returns {string} - Sanitized HTML
 */
export const sanitizeHtml = (html, configType = "default") => {
  if (!html || typeof html !== "string") {
    return "";
  }

  let config;
  switch (configType) {
    case "strict":
      config = strictConfig;
      break;
    case "rich":
      config = richConfig;
      break;
    default:
      config = defaultConfig;
  }

  return DOMPurify.sanitize(html, config);
};

/**
 * Sanitize HTML for use with SafeHtml / dangerouslySetInnerHTML
 * @param {string} html - The HTML content to sanitize
 * @param {string} configType - Configuration type
 * @returns {object} - Object with __html property for React
 */
export const sanitizeForReact = (html, configType = "default") => {
  return {
    __html: sanitizeHtml(html, configType),
  };
};

/**
 * Remove HTML tags and return plain text
 * @param {string} html - The HTML content
 * @returns {string} - Plain text content
 */
export const stripHtml = (html) => {
  if (!html || typeof html !== "string") {
    return "";
  }
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
};

export default {
  sanitizeHtml,
  sanitizeForReact,
  stripHtml,
};
